import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessClientOrigin, ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import AccountStatusFilterForm from 'search/filters/AccountStatusFilterForm';
import AminoAcidsFilterForm from 'search/filters/AminoAcidsFilterForm';
import AncestorStorableFilterForm from 'search/filters/AncestorStorableFilterForm';
import AnyLinkSchemaFieldFilterForm from 'search/filters/AnyLinkSchemaFieldFilterForm';
import ArchivePurposesFilterForm, {
  ArchivePurposesFilterFormExtraProps,
} from 'search/filters/ArchivePurposesFilterForm';
import ArchiveStatusFilterForm from 'search/filters/ArchiveStatusFilterForm';
import ContainerConcentrationFilterForm from 'search/filters/base/ContainerConcentrationFilterForm';
import ContainerQuantityFilterForm from 'search/filters/base/ContainerQuantityFilterForm';
import StandardEnumFilterForm from 'search/filters/base/StandardEnumFilterForm';
import StandardFilterForm from 'search/filters/base/StandardFilterForm';
import CapabilitiesFilterForm, {
  CapabilitiesFilterFormExtraProps,
} from 'search/filters/CapabilitiesFilterForm';
import ContainerCheckOutStatusFilterForm from 'search/filters/ContainerCheckOutStatusFilterForm';
import ContainerContentSchemaFilterForm from 'search/filters/ContainerContentSchemaFilterForm';
import ContainerContentsFilterForm from 'search/filters/ContainerContentsFilterForm';
import ContainerParentFilterForm from 'search/filters/ContainerParentFilterForm';
import CustomTagFilterForm from 'search/filters/CustomTagFilterForm';
import DatetimeRangeFilterForm from 'search/filters/DatetimeRangeFilterForm';
import FolderFilterForm, {FolderFilterFormExtraProps} from 'search/filters/FolderFilterForm';
import IngredientsFilterForm from 'search/filters/IngredientsFilterForm';
import MonomerFilterForm from 'search/filters/MonomerFilterForm';
import NoteReferenceFilterForm from 'search/filters/NoteReferenceFilterForm';
import {OligoHELMFilterFormDNA, OligoHELMFilterFormRNA} from 'search/filters/OligoHELMFilterForm';
import OperatorOnlyDropdownFilterForm from 'search/filters/OperatorOnlyDropdownFilterForm';
import OrganizationFilterForm, {
  OrganizationFilterFormExtraProps,
} from 'search/filters/OrganizationFilterForm';
import PartSequenceFilterForm from 'search/filters/PartSequenceFilterForm';
import ProcessTaskHasFailedMappingsFilterForm from 'search/filters/ProcessTaskHasFailedMappingsFilterForm';
import ProjectAttributeFilterForm from 'search/filters/ProjectAttributeFilterForm';
import ProjectFolderFilterForm from 'search/filters/ProjectFolderFilterForm';
import PrositeFilterForm from 'search/filters/PrositeFilterForm';
import RegistryCollectionOwnerFilterForm, {
  RegistryCollectionOwnerFilterFormExtraProps,
} from 'search/filters/RegistryCollectionOwnerFilterForm';
import RequestSchemaFilterForm, {
  RequestSchemaFilterFormExtraProps,
} from 'search/filters/RequestSchemaFilterForm';
import RequestStatusFilterForm from 'search/filters/RequestStatusFilterForm';
import RequestTaskSchemaFilterForm, {
  RequestTaskSchemaFilterFormExtraProps,
} from 'search/filters/RequestTaskSchemaFilterForm';
import RequestV2DefinitionFilterForm from 'search/filters/RequestV2DefinitionFilterForm';
import ReviewFilterForm from 'search/filters/ReviewFilterForm';
import ReviewLifecycleTypeFilterForm from 'search/filters/ReviewLifecycleTypeFilterForm';
import RunAvailabilityFilterForm from 'search/filters/RunAvailabilityFilterForm';
import SchemaFieldStandardFilterForm, {
  SchemaFieldFilterFormExtraProps,
} from 'search/filters/SchemaFieldStandardFilterForm';
import SchemaValidationStatusFilterForm from 'search/filters/SchemaValidationStatusFilterForm';
import SequenceBasesFilterForm from 'search/filters/SequenceBasesFilterForm';
import SharedWithFilterForm, {SharedWithFilterFormExtraProps} from 'search/filters/SharedWithFilterForm';
import SingleValueTextFilterForm from 'search/filters/SingleValueTextFilterForm';
import StarredFilterForm from 'search/filters/StarredFilterForm';
import TeamFilterForm, {TeamFilterFormExtraProps} from 'search/filters/TeamFilterForm';
import TemplateCollectionFilterForm from 'search/filters/TemplateCollectionFilterForm';
import TranscriptionsFilterForm from 'search/filters/TranscriptionsFilterForm';
import TypesAndSchemaFilterForm, {
  TypesAndSchemaFilterFormExtraProps,
} from 'search/filters/TypesAndSchemaFilterForm';
import UsersFilterForm from 'search/filters/UsersFilterForm';
import WorkflowStatusFilterForm from 'search/filters/WorkflowStatusFilterForm';
import WorkflowTemplateFilterForm from 'search/filters/WorkflowTemplateFilterForm';
import {Filter, FilterKey, keyToEmptyFilter} from 'search/SearchConstants';
import WorklistTypeFilterForm from 'worklists/sidepanel-components/WorklistTypeFilterForm';

import ConnectionFilterForm from '../ConnectionFilterForm';
import EntriesFilterForm from '../EntriesFilterForm';
import getProcessManagementFilterForm from '../getProcessManagementFilterForm';
import InstitutionFilterForm from '../InstitutionFilterForm';
import InStudyFilterForm from '../InStudyFilterForm';
import PolicyFilterForm, {PolicyFilterFormExtraProps} from '../PolicyFilterForm';
import ProcessClientOriginFilterForm from '../ProcessClientOriginFilterForm';
import ProcessExecutionTypeFilterForm from '../ProcessExecutionTypeFilterForm';
import ProcessTaskSchemaFilterForm, {
  ProcessTaskSchemaFilterFormExtraProps,
} from '../ProcessTaskSchemaFilterForm';
import ProcessTaskStatusFilterFormForAllTaskTypes, {
  ProcedureTaskStatusFilterForm,
  ProcessAppTaskStatusFilterForm,
} from '../ProcessTaskStatusFilterForm';
import QueryApiBioentityIdFilterForm from '../QueryApiBioentityIdFilterForm';
import QueryApiProjectFilterForm from '../QueryApiProjectFilterForm';
import QueryApiRegistryFilterForm from '../QueryApiRegistryFilterForm';
import RegistrationStatusFilterForm from '../RegistrationStatusFilterForm';
import RequestV2SubmissionFilterForm from '../RequestV2SubmissionFilterForm';
import SampleDefinitionFilterForm from '../SampleDefinitionFilterForm';
import SampleRestrictionAccessFilterForm from '../SampleRestrictionAccessFilterForm';
import SampleRestrictionStatusFilterForm from '../SampleRestrictionStatusFilterForm';
import SessionStatusFilterForm from '../SessionStatusFilterForm';
import StudyPhaseFilterForm from '../StudyPhaseFilterForm';
import UsersApiIdFilterForm from '../UsersApiIdFilterForm';
export type FilterComponent = React.ComponentType<{
  filter: Filter;
  onChangeFilter: Function;
  onEnter?: () => void;
  isInvalid?: boolean;
  invalidMessage?: string | null;
  prefinalizedFilters?: Array<Filter>;
  disabled?: boolean;
  // Signfiies if the filter has been modified since the user opened the filter form. This will be
  // set to true whenever the user adds or modifies a filter, and marked false when they apply a secondary
  // filter, or close a primary filter dropdown.
  isDirty?: boolean;
  dependencies?: Array<Filter>;
}>;

export function componentForFilterKey(filterKey: FilterKey): FilterComponent | null {
  const FILTER_KEY_TO_COMPONENT: {[key in FilterKey]: FilterComponent | null} = {
    [FilterKey.PUBLISHING_STATUS]: StandardEnumFilterForm,
    [FilterKey.API_IDS]: StandardFilterForm,
    [FilterKey.POLICY]: PolicyFilterForm,
    [FilterKey.TEMPLATE_COLLECTION]: TemplateCollectionFilterForm,
    [FilterKey.REQUEST_V2_SUBMISSION_STATUS]: StandardEnumFilterForm,
    [FilterKey.REQUEST_V2_DEFINITION]: RequestV2DefinitionFilterForm,
    [FilterKey.IS_FILL_IN_ENTRY]: StandardFilterForm,
    [FilterKey.WORKSHEET]: StandardFilterForm,
    [FilterKey.SAMPLE]: StandardFilterForm,
    [FilterKey.SAMPLE_CONTAINER]: StandardFilterForm,
    [FilterKey.SAMPLE_DEFINITION]: SampleDefinitionFilterForm,
    [FilterKey.DILUTION_FACTOR]: StandardFilterForm,
    [FilterKey.TEST_STATUS]: StandardEnumFilterForm,
    [FilterKey.PROCESSES_IS_SYSTEM_DATA_FILTER]: StandardFilterForm,
    [FilterKey.IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION]: StandardFilterForm,
    [FilterKey.FREEZE_THAW_COUNT]: StandardFilterForm,
    [FilterKey.CREATED_AT]: StandardFilterForm,
    [FilterKey.NAMES]: StandardFilterForm,
    [FilterKey.REQUEST_IDS]: StandardFilterForm,
    [FilterKey.GRID_EMPTY_CONTAINERS]: StandardFilterForm,
    [FilterKey.MODIFIED_AT]: StandardFilterForm,
    [FilterKey.REVIEW_INITIAL_STARTED]: StandardFilterForm,
    [FilterKey.REVIEW_STARTED]: StandardFilterForm,
    [FilterKey.REVIEW_COMPLETED]: StandardFilterForm,
    [FilterKey.EXPIRATION_DATE]: StandardFilterForm,
    [FilterKey.IS_EXPIRED]: StandardFilterForm,
    [FilterKey.ACCOUNT_STATUS]: AccountStatusFilterForm,
    [FilterKey.ANY_LINK_SCHEMA_FIELD]: AnyLinkSchemaFieldFilterForm,
    [FilterKey.ANCESTOR_FOLDER]: FolderFilterForm,
    [FilterKey.ANCESTOR_STORABLE]: AncestorStorableFilterForm,
    [FilterKey.ANNOTATION]: StandardFilterForm,
    [FilterKey.ANNOTATION_BASES]: StandardFilterForm,
    [FilterKey.ANNOTATION_RESIDUES]: StandardFilterForm,
    [FilterKey.ARCHIVE_PURPOSES]: ArchivePurposesFilterForm,
    [FilterKey.ARCHIVE_STATUS]: ArchiveStatusFilterForm,
    [FilterKey.ASSAY_SCHEMA]: RequestSchemaFilterForm,
    [FilterKey.ASSAY_TEMPLATE]: StandardFilterForm,
    [FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS]: getProcessManagementFilterForm(
      ModelTypeConstants.ASSAY_RUN_TASK,
      [
        {
          ...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN),
          value: ProcessClientOrigin.ASSAYS,
        },
      ]
    ),
    [FilterKey.ASSAY_METHOD_NAME]: StandardFilterForm,
    [FilterKey.ASSAY_RUN_CONDITION_NAMES]: StandardFilterForm,
    [FilterKey.ASSAY_RUN_REPLICATE_NAMES]: StandardFilterForm,
    [FilterKey.AUTHOR]: UsersFilterForm,
    [FilterKey.BARCODES]: StandardFilterForm,
    [FilterKey.BATCH_TAG]: null,
    [FilterKey.BIOENTITY_LABELS]: StandardFilterForm,
    [FilterKey.BLAST]: null,
    [FilterKey.CAPABILITIES]: CapabilitiesFilterForm,
    [FilterKey.CHEMICAL_STRUCTURE]: null,
    [FilterKey.CONNECTION]: ConnectionFilterForm,
    [FilterKey.CONNECTION_ADAPTER]: StandardEnumFilterForm,
    [FilterKey.CONNECTION_GATEWAY]: StandardEnumFilterForm,
    [FilterKey.CONTAINER_CHECK_OUT_ASSIGNEE]: StandardFilterForm,
    [FilterKey.CONTAINER_CHECK_OUT_STATUS]: ContainerCheckOutStatusFilterForm,
    [FilterKey.CONTAINER_CONCENTRATION]: ContainerConcentrationFilterForm,
    [FilterKey.CONTAINER_CONTENTS]: ContainerContentsFilterForm,
    [FilterKey.CONTAINER_CONTENT_SCHEMA]: ContainerContentSchemaFilterForm,
    [FilterKey.CONTAINER_PARENT]: ContainerParentFilterForm,
    [FilterKey.CONTAINER_QUANTITY]: ContainerQuantityFilterForm,
    [FilterKey.CREATION_ORIGIN_ENTRY]: EntriesFilterForm,
    [FilterKey.CREATOR]: UsersFilterForm,
    [FilterKey.CUSTOM_TAG]: CustomTagFilterForm,
    [FilterKey.ENTRY_TEMPLATE]: null,
    [FilterKey.FOLDER]: null,
    [FilterKey.FOLDER_SECTION]: null,
    [FilterKey.GRID_EMPTY_POSITIONS]: SingleValueTextFilterForm,
    // This filter only exists to support expanding groups when using group by, so should never
    // be exposed in the UI.
    [FilterKey.GROUP_BY_KEY]: null,
    [FilterKey.HIDDEN]: null,
    [FilterKey.INGREDIENTS]: IngredientsFilterForm,
    [FilterKey.INSTITUTION]: InstitutionFilterForm,
    [FilterKey.INVENTORY_VALIDATION_ERROR_LABEL]: StandardEnumFilterForm,
    [FilterKey.INVENTORY_VALIDATION_STATUS]: SchemaValidationStatusFilterForm,
    [FilterKey.ITEM_IN_WORKLIST]: null,
    [FilterKey.LINEAGE_PARENT_API_IDENTIFIER]: StandardFilterForm,
    [FilterKey.MONOMER]: MonomerFilterForm,
    [FilterKey.NOTE_REFERENCE]: NoteReferenceFilterForm,
    [FilterKey.ORGANIZATION]: OrganizationFilterForm,
    [FilterKey.OLIGO_HELM_DNA]: OligoHELMFilterFormDNA,
    [FilterKey.OLIGO_HELM_RNA]: OligoHELMFilterFormRNA,
    [FilterKey.OWNER]: StandardFilterForm,
    [FilterKey.OWNER_OR_COLLABORATOR]: null,
    [FilterKey.PARENT_STORABLE]: null,
    [FilterKey.AA_PART_SEQUENCE]: PartSequenceFilterForm,
    [FilterKey.PART_SEQUENCE]: PartSequenceFilterForm,
    [FilterKey.RNA_PART_SEQUENCE]: PartSequenceFilterForm,
    [FilterKey.TRANSCRIPTIONS]: TranscriptionsFilterForm,
    [FilterKey.RECIPE_TEMPLATE]: StandardFilterForm,
    [FilterKey.PROCEDURE_TASK_STATUS]: ProcedureTaskStatusFilterForm,
    [FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS]: getProcessManagementFilterForm(
      ModelTypeConstants.RECIPE_RUN_TASK,
      [
        {
          ...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN),
          value: ProcessClientOrigin.RECIPES,
        },
      ]
    ),
    [FilterKey.PROCESS]: getProcessManagementFilterForm(ModelTypeConstants.PROCESS, [
      {
        ...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN),
        value: ProcessClientOrigin.PROCESSES,
      },
    ]),
    [FilterKey.RECIPE_METHOD_NAME]: StandardFilterForm,
    [FilterKey.RECIPE_RUN_CONDITION_NAMES]: StandardFilterForm,
    [FilterKey.RECIPE_RUN_REPLICATE_NAMES]: StandardFilterForm,
    [FilterKey.REQUEST_V2_SUBMISSION]: RequestV2SubmissionFilterForm,
    [FilterKey.PROCESS_CLIENT_ORIGIN]: ProcessClientOriginFilterForm,
    [FilterKey.PROCESS_EXECUTION_TYPE]: ProcessExecutionTypeFilterForm,
    [FilterKey.PROCESS_RESPONSIBLE_TEAM]: StandardFilterForm,
    [FilterKey.PROCESS_STATUS]: ProcessAppTaskStatusFilterForm,
    [FilterKey.PROCESS_TASK]: getProcessManagementFilterForm(ModelTypeConstants.PROCESS_TASK, [
      {
        ...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN),
        value: ProcessClientOrigin.PROCESSES,
      },
    ]),
    [FilterKey.PROCESS_TASK_ASSIGNEE]: StandardFilterForm,
    [FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE]: StandardFilterForm,
    [FilterKey.PROCESS_TASK_EXECUTION_ORIGIN_ENTRY]: EntriesFilterForm,
    [FilterKey.PROCESS_TASK_HAS_FAILED_MAPPINGS]: ProcessTaskHasFailedMappingsFilterForm,
    [FilterKey.PROCESS_TASK_STATUS]: ProcessAppTaskStatusFilterForm,
    [FilterKey.PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES]: ProcessTaskStatusFilterFormForAllTaskTypes,
    [FilterKey.PROCESS_TASK_RESPONSIBLE_PARTIES]: StandardFilterForm,
    [FilterKey.PROCESS_WATCHER]: StandardFilterForm,
    [FilterKey.PROJECT]: null,
    [FilterKey.PROJECT_ATTRIBUTE]: ProjectAttributeFilterForm,
    [FilterKey.PROJECT_FOLDER]: ProjectFolderFilterForm,
    [FilterKey.PROTEIN_AMINO_ACIDS]: AminoAcidsFilterForm,
    [FilterKey.AA_SEQUENCE_PROSITE]: PrositeFilterForm,
    [FilterKey.ALPHAFOLD_STRUCTURE]: OperatorOnlyDropdownFilterForm,
    [FilterKey.REGISTRY]: null,
    [FilterKey.REGISTRY_COLLECTION_OWNER]: RegistryCollectionOwnerFilterForm,
    [FilterKey.SAMPLE_RESTRICTION_ACCESS]: SampleRestrictionAccessFilterForm,
    [FilterKey.SAMPLE_RESTRICTION_STATUS]: SampleRestrictionStatusFilterForm,
    [FilterKey.REQUEST_ASSIGNEE]: StandardFilterForm,
    [FilterKey.REQUEST_REQUESTOR]: UsersFilterForm,
    [FilterKey.REQUEST_STATUS]: RequestStatusFilterForm,
    [FilterKey.REQUEST_TASK_SCHEMA]: RequestTaskSchemaFilterForm,
    [FilterKey.REVIEW]: ReviewFilterForm,
    [FilterKey.REVIEW_LIFECYCLE]: StandardEnumFilterForm,
    [FilterKey.REVIEW_LIFECYCLE_TYPE]: ReviewLifecycleTypeFilterForm,
    [FilterKey.ROOT_PROCESS]: getProcessManagementFilterForm(ModelTypeConstants.PROCESS, [
      {
        ...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN),
        value: ProcessClientOrigin.PROCESSES,
      },
      {...keyToEmptyFilter(FilterKey.PROCESS_EXECUTION_TYPE), value: [ProcessExecutionType.FLOWCHART]},
    ]),
    [FilterKey.ROOT_PROCESS_TASK_SCHEMA]: ProcessTaskSchemaFilterForm,
    [FilterKey.ROOT_PROCESS_CREATOR]: StandardFilterForm,
    [FilterKey.ROOT_PROCESS_WATCHER]: StandardFilterForm,
    [FilterKey.RUN_AVAILABILITY]: RunAvailabilityFilterForm,
    [FilterKey.SAMPLE_AUTHORIZED_USERS]: StandardFilterForm,
    [FilterKey.SAMPLE_OWNERS]: StandardFilterForm,
    [FilterKey.SCHEMA]: null,
    [FilterKey.SCHEMA_INTERFACE]: null,
    [FilterKey.SCHEMA_FIELD]: SchemaFieldStandardFilterForm,
    [FilterKey.SCHEMA_VALIDATION_ERROR_LABEL]: StandardEnumFilterForm,
    [FilterKey.SCHEMA_VALIDATION_STATUS]: SchemaValidationStatusFilterForm,
    [FilterKey.REGISTRATION_STATUS]: RegistrationStatusFilterForm,
    [FilterKey.SEQUENCE_BASES]: SequenceBasesFilterForm,
    [FilterKey.SESSION_NAME]: SingleValueTextFilterForm,
    [FilterKey.SESSION_STATUS]: SessionStatusFilterForm,
    [FilterKey.SESSION_MODIFIED_AT]: DatetimeRangeFilterForm,
    [FilterKey.SHARED_WITH]: SharedWithFilterForm,
    [FilterKey.STARRED]: StarredFilterForm,
    [FilterKey.STUDY_PHASE]: StudyPhaseFilterForm,
    [FilterKey.IN_STUDY]: InStudyFilterForm,
    [FilterKey.TAG]: null,
    [FilterKey.TEAM]: TeamFilterForm,
    [FilterKey.TOP_LEVEL_PERMISSIONS]: null,
    [FilterKey.TYPES]: TypesAndSchemaFilterForm,
    [FilterKey.TYPE]: null,
    [FilterKey.WORKFLOW_STATUS]: WorkflowStatusFilterForm,
    [FilterKey.WORKFLOW_TEMPLATE]: WorkflowTemplateFilterForm,
    [FilterKey.WORKLIST_TYPE]: WorklistTypeFilterForm,
    [FilterKey.QUERY_API_BIOENTITY_ID]: QueryApiBioentityIdFilterForm,
    [FilterKey.QUERY_API_ARCHIVE_REASON]: ArchivePurposesFilterForm,
    [FilterKey.QUERY_API_MODIFIED_AT]: DatetimeRangeFilterForm,
    [FilterKey.QUERY_API_CREATED_AT]: DatetimeRangeFilterForm,
    [FilterKey.QUERY_API_AUTHOR]: UsersApiIdFilterForm,
    [FilterKey.QUERY_API_CREATOR]: UsersApiIdFilterForm,
    [FilterKey.QUERY_API_PROJECT]: QueryApiProjectFilterForm,
    [FilterKey.QUERY_API_REGISTRY]: QueryApiRegistryFilterForm,
  };
  return FILTER_KEY_TO_COMPONENT[filterKey];
}

export interface AncestorFolderFilterOption {
  filterKey: FilterKey.ANCESTOR_FOLDER;
  extraProps?: FolderFilterFormExtraProps;
}

export interface ArchivePurposesFilterOption {
  filterKey: FilterKey.ARCHIVE_PURPOSES | FilterKey.QUERY_API_ARCHIVE_REASON;
  extraProps: ArchivePurposesFilterFormExtraProps;
}
export interface CapabilitiesFilterFormOption {
  filterKey: FilterKey.CAPABILITIES;
  extraProps: CapabilitiesFilterFormExtraProps;
}
export interface OrganizationFilterFormOption {
  filterKey: FilterKey.ORGANIZATION;
  extraProps: OrganizationFilterFormExtraProps;
}
export interface PolicyFilterFormOption {
  filterKey: FilterKey.POLICY;
  extraProps: PolicyFilterFormExtraProps;
}
export interface RootProcessTaskSchemaFilterOption {
  filterKey: FilterKey.ROOT_PROCESS_TASK_SCHEMA;
  extraProps: ProcessTaskSchemaFilterFormExtraProps;
}
export interface RegistryCollectionOwnerFilterOption {
  filterKey: FilterKey.REGISTRY_COLLECTION_OWNER;
  extraProps: RegistryCollectionOwnerFilterFormExtraProps;
}
export interface RequestSchemaFilterOption {
  filterKey: FilterKey.ASSAY_SCHEMA;
  extraProps: RequestSchemaFilterFormExtraProps;
}
export interface RequestTaskSchemaFilterOption {
  filterKey: FilterKey.REQUEST_TASK_SCHEMA;
  extraProps: RequestTaskSchemaFilterFormExtraProps;
}
export interface SchemaFieldFilterOption {
  filterKey: FilterKey.SCHEMA_FIELD;
  extraProps: SchemaFieldFilterFormExtraProps;
}
export interface TeamFilterFormOption {
  filterKey: FilterKey.TEAM;
  extraProps: TeamFilterFormExtraProps;
}
export interface TypesAndSchemaFilterOption {
  filterKey: FilterKey.TYPES;
  extraProps: TypesAndSchemaFilterFormExtraProps;
}
export interface SharedWithFilterFilterOption {
  filterKey: FilterKey.SHARED_WITH;
  extraProps: SharedWithFilterFormExtraProps;
}

interface QueryApiBioentityIdFilterFormOption {
  filterKey: FilterKey.QUERY_API_BIOENTITY_ID;
  extraProps: {schemaId: string};
}

export type FilterOptionWithExtraProps =
  | AncestorFolderFilterOption
  | ArchivePurposesFilterOption
  | CapabilitiesFilterFormOption
  | RootProcessTaskSchemaFilterOption
  | RegistryCollectionOwnerFilterOption
  | RequestSchemaFilterOption
  | RequestTaskSchemaFilterOption
  | SchemaFieldFilterOption
  | SharedWithFilterFilterOption
  | TeamFilterFormOption
  | TypesAndSchemaFilterOption
  | QueryApiBioentityIdFilterFormOption
  | OrganizationFilterFormOption
  | PolicyFilterFormOption;

export interface FilterOptionWithoutExtraProps {
  filterKey: Exclude<FilterKey, FilterOptionWithExtraProps['filterKey']>;
  extraProps?: never;
}

export type FilterOption = FilterOptionWithExtraProps | FilterOptionWithoutExtraProps;
